// src/components/policies/AddAnnuityModal.jsx
// Modal for adding/editing annuity policies

import React, { useState, useEffect } from 'react';
import { X, PiggyBank, Loader2, Calendar, AlertTriangle } from 'lucide-react';
import { createAnnuityPolicy, updateAnnuityPolicy } from '../../api';

const ANNUITY_TYPES = [
  { value: 'fixed', label: 'Fixed Annuity' },
  { value: 'fixed_indexed', label: 'Fixed Indexed Annuity (FIA)' },
  { value: 'variable', label: 'Variable Annuity' },
  { value: 'immediate', label: 'Immediate Annuity (SPIA)' },
  { value: 'deferred', label: 'Deferred Annuity' }
];

const STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'annuitized', label: 'Annuitized' },
  { value: 'surrendered', label: 'Surrendered' },
  { value: 'matured', label: 'Matured' }
];

export default function AddAnnuityModal({ isOpen, onClose, clientId, policy, onSuccess }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    carrier: '',
    annuityType: 'fixed_indexed',
    policyNumber: '',
    premiumAmount: '',
    currentValue: '',
    effectiveDate: '',
    surrenderPeriodYears: '',
    surrenderEndDate: '',
    currentSurrenderChargePct: '',
    monthlyIncome: '',
    incomeStartDate: '',
    beneficiaryName: '',
    beneficiaryRelationship: '',
    status: 'active',
    notes: ''
  });

  const isEditing = !!policy;
  const isImmediate = form.annuityType === 'immediate';

  useEffect(() => {
    if (isOpen) {
      if (policy) {
        setForm({
          carrier: policy.carrier || '',
          annuityType: policy.annuityType || 'fixed_indexed',
          policyNumber: policy.policyNumber || '',
          premiumAmount: policy.premiumAmount || '',
          currentValue: policy.currentValue || '',
          effectiveDate: policy.effectiveDate?.split('T')[0] || '',
          surrenderPeriodYears: policy.surrenderPeriodYears || '',
          surrenderEndDate: policy.surrenderEndDate?.split('T')[0] || '',
          currentSurrenderChargePct: policy.currentSurrenderChargePct || '',
          monthlyIncome: policy.monthlyIncome || '',
          incomeStartDate: policy.incomeStartDate?.split('T')[0] || '',
          beneficiaryName: policy.beneficiaryName || '',
          beneficiaryRelationship: policy.beneficiaryRelationship || '',
          status: policy.status || 'active',
          notes: policy.notes || ''
        });
      } else {
        setForm({
          carrier: '',
          annuityType: 'fixed_indexed',
          policyNumber: '',
          premiumAmount: '',
          currentValue: '',
          effectiveDate: '',
          surrenderPeriodYears: '',
          surrenderEndDate: '',
          currentSurrenderChargePct: '',
          monthlyIncome: '',
          incomeStartDate: '',
          beneficiaryName: '',
          beneficiaryRelationship: '',
          status: 'active',
          notes: ''
        });
      }
    }
  }, [isOpen, policy]);

  // Auto-calculate surrender end date
  useEffect(() => {
    if (form.effectiveDate && form.surrenderPeriodYears && !form.surrenderEndDate) {
      const startDate = new Date(form.effectiveDate);
      const endDate = new Date(startDate);
      endDate.setFullYear(endDate.getFullYear() + parseInt(form.surrenderPeriodYears));
      setForm(prev => ({
        ...prev,
        surrenderEndDate: endDate.toISOString().split('T')[0]
      }));
    }
  }, [form.effectiveDate, form.surrenderPeriodYears]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.carrier || !form.annuityType) {
      alert('Please fill in required fields');
      return;
    }

    try {
      setSaving(true);

      const data = {
        ...form,
        premiumAmount: form.premiumAmount ? parseFloat(form.premiumAmount) : null,
        currentValue: form.currentValue ? parseFloat(form.currentValue) : null,
        surrenderPeriodYears: form.surrenderPeriodYears ? parseInt(form.surrenderPeriodYears) : null,
        currentSurrenderChargePct: form.currentSurrenderChargePct ? parseFloat(form.currentSurrenderChargePct) : null,
        monthlyIncome: form.monthlyIncome ? parseFloat(form.monthlyIncome) : null,
        effectiveDate: form.effectiveDate || null,
        surrenderEndDate: form.surrenderEndDate || null,
        incomeStartDate: form.incomeStartDate || null
      };

      if (isEditing) {
        await updateAnnuityPolicy(clientId, policy.id, data);
      } else {
        await createAnnuityPolicy(clientId, data);
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('[AddAnnuityModal] Save error:', err);
      alert('Failed to save policy: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <PiggyBank className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">
                {isEditing ? 'Edit' : 'Add'} Annuity
              </h2>
              <p className="text-sm text-slate-500">
                {isEditing ? 'Update annuity details' : 'Enter annuity information'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Annuity Type & Carrier */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Annuity Type *
              </label>
              <select
                value={form.annuityType}
                onChange={(e) => handleChange('annuityType', e.target.value)}
                required
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                {ANNUITY_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Carrier *
              </label>
              <input
                type="text"
                value={form.carrier}
                onChange={(e) => handleChange('carrier', e.target.value)}
                placeholder="e.g., Athene"
                required
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
          </div>

          {/* Policy Number */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Policy Number
            </label>
            <input
              type="text"
              value={form.policyNumber}
              onChange={(e) => handleChange('policyNumber', e.target.value)}
              placeholder="e.g., AN-2024-12345"
              className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>

          {/* Premium & Current Value */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Premium Amount
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                <input
                  type="number"
                  value={form.premiumAmount}
                  onChange={(e) => handleChange('premiumAmount', e.target.value)}
                  placeholder="100,000"
                  className="w-full pl-8 pr-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Current Value
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                <input
                  type="number"
                  value={form.currentValue}
                  onChange={(e) => handleChange('currentValue', e.target.value)}
                  placeholder="105,000"
                  className="w-full pl-8 pr-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
              </div>
            </div>
          </div>

          {/* Effective Date */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Effective Date
            </label>
            <input
              type="date"
              value={form.effectiveDate}
              onChange={(e) => handleChange('effectiveDate', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>

          {/* Surrender Period Section */}
          {!isImmediate && (
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl space-y-4">
              <div className="flex items-center gap-2 text-orange-700">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">Surrender Period</span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Period (Years)
                  </label>
                  <input
                    type="number"
                    value={form.surrenderPeriodYears}
                    onChange={(e) => handleChange('surrenderPeriodYears', e.target.value)}
                    placeholder="7"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Current Charge %
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.1"
                      value={form.currentSurrenderChargePct}
                      onChange={(e) => handleChange('currentSurrenderChargePct', e.target.value)}
                      placeholder="7.0"
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Surrender End Date
                </label>
                <input
                  type="date"
                  value={form.surrenderEndDate}
                  onChange={(e) => handleChange('surrenderEndDate', e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
                />
              </div>
            </div>
          )}

          {/* Income Details (for immediate annuities) */}
          {isImmediate && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl space-y-4">
              <div className="flex items-center gap-2 text-emerald-700">
                <Calendar className="w-4 h-4" />
                <span className="text-sm font-medium">Income Details</span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Monthly Income
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <input
                      type="number"
                      value={form.monthlyIncome}
                      onChange={(e) => handleChange('monthlyIncome', e.target.value)}
                      placeholder="1,500"
                      className="w-full pl-8 pr-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Income Start Date
                  </label>
                  <input
                    type="date"
                    value={form.incomeStartDate}
                    onChange={(e) => handleChange('incomeStartDate', e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Beneficiary */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Beneficiary Name
              </label>
              <input
                type="text"
                value={form.beneficiaryName}
                onChange={(e) => handleChange('beneficiaryName', e.target.value)}
                placeholder="Jane Doe"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Relationship
              </label>
              <input
                type="text"
                value={form.beneficiaryRelationship}
                onChange={(e) => handleChange('beneficiaryRelationship', e.target.value)}
                placeholder="Spouse"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
          </div>

          {/* Status */}
          {isEditing && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Status
              </label>
              <select
                value={form.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                {STATUSES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Notes
            </label>
            <textarea
              value={form.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={3}
              placeholder="Additional notes..."
              className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-xl disabled:opacity-50 transition-colors"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEditing ? 'Save Changes' : 'Add Annuity'}
          </button>
        </div>
      </div>
    </div>
  );
}
